import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Search, Loader2, Plus, MapPin, X as XIcon, Check, Plus as PlusIcon, Trash2 } from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { AdminPagination } from "./AdminPagination";
import { AdminLocationSelector, COUNTRY_NAMES_ES } from "./AdminLocationSelector";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOB_TYPES = [
  { value: "full-time", label: "Tiempo completo" },
  { value: "part-time", label: "Medio tiempo" },
  { value: "contract", label: "Contrato" },
  { value: "temporary", label: "Temporal" },
  { value: "internship", label: "Prácticas / Pasantía" },
  { value: "freelance", label: "Por proyecto / Freelance" },
  { value: "hourly", label: "Por horas" },
  { value: "seasonal", label: "Estacional" },
];
const CURRENCIES = [
  { code: "EUR", symbol: "€" }, { code: "USD", symbol: "US$" },
  { code: "UYU", symbol: "$U" }, { code: "ARS", symbol: "AR$" }, { code: "BRL", symbol: "R$" },
];
const DEFAULT_CURRENCY_BY_COUNTRY = { ES: "EUR", UY: "UYU", US: "USD", AR: "ARS", BR: "BRL" };

const EMPTY_JOB_FORM = {
  title: "", company_id: "", company_name: "", description: "",
  requirements: [], responsibilities: [], job_type: "full-time", remote: false,
  salary_min: "", salary_max: "", salary_currency: "USD",
  country_code: null, admin1_code: null, admin1_name: null, admin2_code: null, admin2_name: null,
  city: null, latitude: null, longitude: null, location_verified: false,
};

function formatSalary(j) {
  const symbol = CURRENCIES.find((c) => c.code === j.salary_currency)?.symbol || j.salary_currency || "";
  if (!j.salary_min && !j.salary_max) return "A convenir";
  if (j.salary_min && j.salary_max) return `${symbol}${(j.salary_min / 1000).toFixed(0)}K–${symbol}${(j.salary_max / 1000).toFixed(0)}K`;
  return `${symbol}${((j.salary_min || j.salary_max) / 1000).toFixed(0)}K`;
}

function ListEditor({ label, items, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (!draft.trim()) return; onChange([...items, draft.trim()]); setDraft(""); };
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 mb-1 block">{label}</label>
      <div className="flex gap-2 mb-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button type="button" variant="outline" size="icon" onClick={add}><PlusIcon className="h-4 w-4" /></Button>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5">
            <span className="truncate">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 shrink-0">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminJobsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFilter = searchParams.get("company_id") || undefined;
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState(companyIdFilter || "");
  const [modalityFilter, setModalityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [companies, setCompanies] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_JOB_FORM);
  const [creating, setCreating] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const limit = 15;

  useEffect(() => {
    axios.get(`${API}/admin/companies`, { headers: getAdminAuthHeaders(), params: { limit: 200 } })
      .then((res) => setCompanies(res.data.companies))
      .catch(() => setCompanies([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs`, {
        headers: getAdminAuthHeaders(),
        params: {
          keyword: keyword || undefined, status: statusFilter || undefined,
          country_code: countryFilter || undefined, company_id: companyFilter || undefined,
          remote: modalityFilter === "remote" ? true : modalityFilter === "onsite" ? false : undefined,
          job_type: typeFilter || undefined,
          page, limit,
        },
      });
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch (err) {
      toast.error("No se pudieron cargar los empleos");
      setJobs([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [keyword, statusFilter, countryFilter, companyFilter, modalityFilter, typeFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [keyword, statusFilter, countryFilter, companyFilter, modalityFilter, typeFilter]);

  // Orden es client-side sobre la página cargada; modalidad/tipo/estado/país ya
  // filtran en el backend.
  const visibleJobs = [...jobs].sort((a, b) => {
    if (sortBy === "title") return (a.title || "").localeCompare(b.title || "");
    if (sortBy === "company") return (a.company_name || "").localeCompare(b.company_name || "");
    if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
    return new Date(b.posted_date || 0) - new Date(a.posted_date || 0);
  });

  const toggleStatus = async (j) => {
    setActionLoading(j.job_id);
    try {
      const action = j.status === "active" ? "pause" : "reactivate";
      await axios.post(`${API}/admin/jobs/${j.job_id}/${action}`, {}, { headers: getAdminAuthHeaders() });
      toast.success(j.status === "active" ? `${j.title} pausado` : `${j.title} reactivado`);
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail));
    } finally {
      setActionLoading("");
    }
  };
  const filteredCompanies = companies.filter((c) => c.name.toLowerCase().includes(companySearch.toLowerCase()));

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) { toast.error("El título es obligatorio"); return; }
    if (!createForm.company_id) { toast.error("Seleccioná una empresa existente"); return; }
    setCreating(true);
    try {
      const payload = {
        ...createForm,
        salary_min: createForm.salary_min ? Number(createForm.salary_min) : null,
        salary_max: createForm.salary_max ? Number(createForm.salary_max) : null,
      };
      delete payload.company_name;
      delete payload.location;
      await axios.post(`${API}/admin/jobs`, payload, { headers: getAdminAuthHeaders() });
      toast.success(`Empleo "${createForm.title}" creado`);
      setCreateOpen(false);
      setCreateForm(EMPTY_JOB_FORM);
      setCompanySearch("");
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const patchLocation = (patch) => {
    setCreateForm((f) => {
      const next = { ...f, ...patch };
      if (patch.country_code) next.salary_currency = DEFAULT_CURRENCY_BY_COUNTRY[patch.country_code] || f.salary_currency;
      return next;
    });
  };

  return (
    <div data-testid="admin-jobs-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Empleos</h1>
          <p className="text-sm text-slate-500">Todos los empleos publicados en la plataforma</p>
        </div>
        <Button data-testid="admin-jobs-create-button" onClick={() => setCreateOpen(true)} className="bg-[#028CF8] hover:bg-[#0270C5] shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Crear empleo
        </Button>
      </div>

      {companyIdFilter && (
        <div data-testid="admin-jobs-company-filter-chip" className="mt-3 mb-1 inline-flex items-center gap-2 text-xs font-semibold bg-blue-50 text-[#028CF8] px-3 py-1.5 rounded-full">
          Filtrando empleos de esta empresa
          <Link to="/admin/jobs" data-testid="admin-jobs-clear-company-filter" className="underline">Ver todos</Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 mt-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="admin-jobs-search-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Buscar por título o empresa..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <select data-testid="admin-jobs-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="paused">Pausado</option>
          <option value="closed">Cerrado</option>
          <option value="archived">Archivado</option>
        </select>
        <select data-testid="admin-jobs-country-filter" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los países</option>
          {Object.entries(COUNTRY_NAMES_ES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <select data-testid="admin-jobs-company-filter" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white max-w-[180px]">
          <option value="">Todas las empresas</option>
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
        </select>
        <select data-testid="admin-jobs-modality-filter" value={modalityFilter} onChange={(e) => setModalityFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Toda modalidad</option>
          <option value="remote">Remoto</option>
          <option value="onsite">Presencial</option>
        </select>
        <select data-testid="admin-jobs-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todo tipo</option>
          {JOB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select data-testid="admin-jobs-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="recent">Ordenar: Fecha de publicación</option>
          <option value="title">Ordenar: Título (A-Z)</option>
          <option value="company">Ordenar: Empresa (A-Z)</option>
          <option value="status">Ordenar: Estado</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm table-fixed" data-testid="admin-jobs-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-3 w-[24%]">Empleo</th>
              <th className="text-left px-3 py-3 w-[18%]">Ubicación / País</th>
              <th className="text-left px-3 py-3 w-[15%]">Modalidad / Tipo</th>
              <th className="text-left px-3 py-3 w-[15%]">Estado / Publicado</th>
              <th className="text-left px-3 py-3 w-[10%]">Salario</th>
              <th className="text-left px-3 py-3 w-[18%]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleJobs.map((j) => (
              <tr key={j.job_id} data-testid={`admin-job-row-${j.job_id}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-3 py-2.5">
                  <p className="font-medium text-slate-900 truncate" title={j.title}>{j.title}</p>
                  <p className="text-xs text-slate-500 truncate" title={j.company_name}>{j.company_name}</p>
                </td>
                <td className="px-3 py-2.5 text-slate-600 truncate">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{j.city || j.location || "—"}{j.country_code ? ` · ${COUNTRY_NAMES_ES[j.country_code] || j.country_code}` : ""}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-600 truncate">
                  <p className="truncate">{j.remote ? "Remoto" : "Presencial"}</p>
                  <p className="text-xs text-slate-500 truncate">{JOB_TYPES.find((t) => t.value === j.job_type)?.label || j.job_type}</p>
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge value={j.status} testId={`admin-job-status-${j.job_id}`} />
                  <p className="text-xs text-slate-500 mt-1">{j.posted_date ? new Date(j.posted_date).toLocaleDateString() : "—"}</p>
                </td>
                <td className="px-3 py-2.5 text-slate-600 truncate">{formatSalary(j)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-2.5 flex-wrap">
                    <button data-testid={`admin-job-view-${j.job_id}`} onClick={() => navigate(`/admin/jobs/${j.job_id}`)} className="text-xs font-semibold text-[#028CF8] hover:underline">Ver</button>
                    {j.status === "active" && (
                      <button data-testid={`admin-job-toggle-status-${j.job_id}`} onClick={() => toggleStatus(j)} disabled={actionLoading === j.job_id}
                        className="text-xs font-semibold hover:underline text-amber-600">
                        {actionLoading === j.job_id ? "..." : "Pausar"}
                      </button>
                    )}
                    {(j.status === "paused" || j.status === "archived") && (
                      <button data-testid={`admin-job-reactivate-${j.job_id}`} onClick={() => toggleStatus(j)} disabled={actionLoading === j.job_id}
                        className="text-xs font-semibold hover:underline text-emerald-600">
                        {actionLoading === j.job_id ? "..." : "Reactivar"}
                      </button>
                    )}
                    {j.status !== "archived" && (
                      <button data-testid={`admin-job-archive-${j.job_id}`} onClick={() => navigate(`/admin/jobs/${j.job_id}?archive=1`)} className="text-xs font-semibold text-red-600 hover:underline">
                        Archivar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && visibleJobs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}
      </div>

      <AdminPagination page={page} total={total} limit={limit} onPageChange={setPage} testidPrefix="admin-jobs" />

      {/* ── Crear empleo — drawer ancho (vista previa) ── */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent data-testid="admin-job-create-sheet" className="w-full sm:max-w-[560px] md:max-w-[680px] p-0 flex flex-col">
          <form onSubmit={submitCreate} className="flex flex-col h-full">
            <SheetHeader className="p-6 pb-4 border-b border-slate-100 shrink-0">
              <SheetTitle className="font-poppins text-xl">Crear empleo</SheetTitle>
              <p className="text-sm text-slate-500">Publicación manual de un empleo para una empresa existente</p>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Título *</label>
                <Input data-testid="admin-job-create-title" required value={createForm.title} onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="relative">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Empresa existente *</label>
                <Input
                  data-testid="admin-job-create-company-search"
                  value={createForm.company_id ? createForm.company_name : companySearch}
                  onChange={(e) => { setCompanySearch(e.target.value); setCreateForm((f) => ({ ...f, company_id: "", company_name: "" })); }}
                  placeholder="Buscá una empresa por nombre..."
                />
                {companySearch && !createForm.company_id && (
                  <div data-testid="admin-job-company-suggestions" className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCompanies.length > 0 ? filteredCompanies.slice(0, 8).map((c) => (
                      <button type="button" key={c.company_id} data-testid={`admin-job-company-option-${c.company_id}`}
                        onClick={() => { setCreateForm((f) => ({ ...f, company_id: c.company_id, company_name: c.name })); setCompanySearch(""); }}
                        className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-[#028CF8]/5 border-b border-slate-50 last:border-0 truncate">
                        {c.name}
                      </button>
                    )) : <p className="px-3.5 py-2.5 text-xs text-slate-600">Sin empresas encontradas. No se pueden crear empresas nuevas desde aquí.</p>}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Descripción</label>
                <Textarea data-testid="admin-job-create-description" value={createForm.description} onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} className="min-h-[90px]" />
              </div>
              <ListEditor label="Requisitos" items={createForm.requirements} onChange={(v) => setCreateForm((f) => ({ ...f, requirements: v }))} placeholder="Ej: 3+ años de experiencia" />
              <ListEditor label="Responsabilidades" items={createForm.responsibilities} onChange={(v) => setCreateForm((f) => ({ ...f, responsibilities: v }))} placeholder="Ej: Liderar el equipo de ventas" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Tipo de empleo</label>
                  <select data-testid="admin-job-create-type" value={createForm.job_type} onChange={(e) => setCreateForm((f) => ({ ...f, job_type: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {JOB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Modalidad</label>
                  <div className="flex gap-2 h-10">
                    {[{ v: false, l: "Presencial" }, { v: true, l: "Remoto" }].map((opt) => (
                      <button key={opt.l} type="button" data-testid={`admin-job-create-modality-${opt.v}`}
                        onClick={() => setCreateForm((f) => ({ ...f, remote: opt.v }))}
                        className={`flex-1 rounded-lg text-xs font-semibold border ${createForm.remote === opt.v ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <AdminLocationSelector
                countryCode={createForm.country_code} admin1Code={createForm.admin1_code} admin1Name={createForm.admin1_name}
                admin2Code={createForm.admin2_code} admin2Name={createForm.admin2_name} city={createForm.city}
                locationVerified={createForm.location_verified} onChange={patchLocation}
              />

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Salario mínimo</label>
                  <Input data-testid="admin-job-create-salary-min" type="number" value={createForm.salary_min} onChange={(e) => setCreateForm((f) => ({ ...f, salary_min: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Salario máximo</label>
                  <Input data-testid="admin-job-create-salary-max" type="number" value={createForm.salary_max} onChange={(e) => setCreateForm((f) => ({ ...f, salary_max: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Moneda</label>
                  <select data-testid="admin-job-create-currency" value={createForm.salary_currency} onChange={(e) => setCreateForm((f) => ({ ...f, salary_currency: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
                  </select>
                </div>
              </div>
            </div>
            <SheetFooter className="p-4 border-t border-slate-100 shrink-0 gap-2">
              <Button type="button" data-testid="admin-job-create-cancel" variant="outline" onClick={() => { setCreateOpen(false); setCreateForm(EMPTY_JOB_FORM); setCompanySearch(""); }}>
                <XIcon className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
              <Button type="submit" data-testid="admin-job-create-submit" disabled={creating} className="bg-[#028CF8] hover:bg-[#0270C5]">
                {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Crear empleo
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
